// Model bytes through the app origin instead of a redirect to R2.
//
// /uploads/<key> answers 302 -> the R2 S3 endpoint, which speaks HTTP/1.1
// only. A browser holds six connections per host, so the viewer tops out
// near 28 files a second whatever its fetch window is - and one SpaceX
// view wants thousands of files (1,377 tiles x 2, out of 118,295 fragment
// GLBs). That cap is why a big model crawls in.
//
// /stream/<key> (back/routes/streamAssets.js) hands back the same bytes
// from this origin, which is behind Cloudflare and speaks h2/h3: the
// browser multiplexes hundreds of streams over one connection, and the
// objects - a version prefix never changes, a fragment is named by its
// content hash - are sent immutable, so the edge serves them after the
// first reader.
//
// Rewriting here rather than at every call site keeps one rule for tiles,
// sidecars, fragments and the manifest. Turn it off in a console with
//   window.__streamProxy = false
// (or localStorage.setItem('bl_stream_proxy', 'off')) and reload: every
// URL goes back to /uploads and the redirect, with no other change.
const UPLOADS = '/uploads/';
const STREAM = '/stream/';

// THE localStorage SWITCHES, READ ONCE PER LOAD (round 11). fetchModel runs
// per request - thousands on a SpaceX load - and read two localStorage keys
// each time. They change only by hand in a console, so they are read once
// and kept until a load starts (resetStreamFlags, from StreamingLoader) or
// another tab changes storage (the 'storage' event). The window.__ switches
// are plain property reads and stay live.
// (Tom's r10 profiles also showed "get cookie" in load tasks: that is NOT
// this path, which never reads document.cookie - a headless staging load
// attributed 706 of 715 cookie reads to gtag.js, see GA4.)
let _flags = null;
let _flagReads = 0;
function storageFlags() {
  if (_flags) return _flags;
  const f = { proxyOff: false, modelsOff: false };
  try {
    if (typeof localStorage !== 'undefined') {
      _flagReads++;
      f.proxyOff = localStorage.getItem('bl_stream_proxy') === 'off';
      f.modelsOff = localStorage.getItem('bl_models_host') === 'off';
    }
  } catch (e) { /* private mode - the defaults stand */ }
  _flags = f;
  return f;
}
export function resetStreamFlags() { _flags = null; }
export function streamFlagReads() { return _flagReads; }
try {
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('storage', resetStreamFlags);
  }
} catch (e) { /* no window */ }

export function streamProxyEnabled() {
  try {
    if (typeof window !== 'undefined' && window.__streamProxy === false) return false;
  } catch (e) { /* the default stands */ }
  return !storageFlags().proxyOff;
}

/** ".../uploads/<key>" -> ".../stream/<key>", when the proxy is on. */
export function toStreamUrl(url) {
  const u = String(url || '');
  if (!u || !streamProxyEnabled()) return u;
  const i = u.indexOf(UPLOADS);
  if (i === -1) return u;
  return `${u.slice(0, i)}${STREAM}${u.slice(i + UPLOADS.length)}`;
}

// --- models.bidlight.com -------------------------------------------
//
// Model geometry also lives in its own R2 bucket, `bidlight-models`,
// served at models.bidlight.com: h2/h3 straight from Cloudflare's edge,
// no hop through this origin at all. Only geometry is in that bucket -
// a version's tiles and the shared fragment library - never the
// invoices and documents `uploads` also holds, which is why the domain
// is on a separate bucket rather than on `uploads`.
//
// An export uses it only when its manifest says `modelsBucket` (set by
// back/utils/modelBucketCopy once that export's tiles and fragments are
// all in the bucket), and only on an origin the bucket's CORS allows.
// Anything else - the manifest itself, sidecars outside tiles/, an
// export not copied yet - keeps going through /stream.
//
// A failed request (404, CORS, network, 5xx) is retried once through
// /stream, so a gap in the bucket costs one extra request, not a hole
// in the model. Repeated NON-404 failures mean the domain itself is not
// reachable from this browser, and it is dropped for the session.
// Off switch: window.__modelsHost = false, or
// localStorage.setItem('bl_models_host', 'off').
export const MODELS_HOST = 'https://models.bidlight.com';
// The origins in the bucket's CORS rule. Matched by hostname rather than
// listed as URLs so the staging origin's URL never appears in a
// production bundle - the deploy gate counts exactly that string.
const MODELS_ORIGIN = /^(?:https:\/\/(?:app|predeployment)\.bidlight\.com|http:\/\/localhost:3000)$/;
// A version's tiles, the project-level tiles the viewers load beside
// <project>/manifest.json.gz, and the shared fragment library.
const MODEL_KEY = /^(?:[0-9a-f]{24}\/(?:versions\/[^/?]+\/)?tiles\/|fragments\/)/i;
const MODELS_HOST_MAX_ERRORS = 20;

const _modelsStats = { direct: 0, fallbacks: 0, errors: 0, disabled: false };

export function modelsHostStats() { return { ..._modelsStats }; }

export function _resetModelsHostStats() {
  _modelsStats.direct = 0; _modelsStats.fallbacks = 0; _modelsStats.errors = 0; _modelsStats.disabled = false;
}

export function modelsHostEnabled() {
  if (_modelsStats.disabled) return false;
  try {
    if (typeof window === 'undefined') return false;
    if (window.__modelsHost === false) return false;
    if (storageFlags().modelsOff) return false;
    return MODELS_ORIGIN.test(window.location.origin);
  } catch (e) { return false; }
}

/** ".../uploads/<model key>" -> "https://models.bidlight.com/<model key>", else null. */
export function toModelsUrl(url) {
  const u = String(url || '');
  const i = u.indexOf(UPLOADS);
  if (i === -1) return null;
  const key = u.slice(i + UPLOADS.length);
  return MODEL_KEY.test(key) ? `${MODELS_HOST}/${key}` : null;
}

/**
 * fetch() for model bytes. `useModelsHost` is the export's manifest flag;
 * without it this is exactly fetch(toStreamUrl(url)).
 */
export async function fetchModel(url, init, useModelsHost = false) {
  const direct = useModelsHost && modelsHostEnabled() ? toModelsUrl(url) : null;
  if (direct) {
    try {
      const res = await fetch(direct, init);
      if (res.ok) { _modelsStats.direct++; return res; }
      if (res.status !== 404) _modelsStats.errors++;
    } catch (e) {
      _modelsStats.errors++;
    }
    _modelsStats.fallbacks++;
    if (_modelsStats.errors >= MODELS_HOST_MAX_ERRORS && !_modelsStats.disabled) {
      _modelsStats.disabled = true;
      console.warn('[streaming] models host unreachable from this browser; using /stream for the session');
    }
  }
  return fetch(toStreamUrl(url), init);
}

// A manifest published straight from the models bucket (direct-to-models
// upload, 2026-09-19) says where its tiles are: `tilesBase:
// 'versions/<exportVersion>/tiles/'`, so nothing is copied to tiles/ at
// promote. Every tile-relative path ("tiles/...") is read from there.
// Anything else in tilesBase is ignored - a manifest cannot point the
// viewer outside its own project's version tiles.
const TILES_BASE = /^versions\/[^/?#]+\/tiles\/$/;
export function applyTilesBase(rel, tilesBase) {
  const r = String(rel || '');
  if (!tilesBase || !TILES_BASE.test(String(tilesBase))) return r;
  const lead = r.match(/^\/*/)[0];
  const body = r.slice(lead.length);
  return /^tiles\//i.test(body) ? `${lead}${tilesBase}${body.slice('tiles/'.length)}` : r;
}
